/**
 * updateChecker — detects when a fresh Frontend build has been
 * deployed and offers the user a one-click "Update Now".
 *
 * Design goals:
 *   1. NO polling of anything auth-scoped. The check reads
 *      ``/asset-manifest.json`` (generated by CRA at build time) with
 *      ``cache: 'no-store'`` — this file changes on every deploy.
 *   2. NO surprise reloads. We only *notify*. The user clicks
 *      "Update Now" (or the update happens on next full navigation).
 *   3. Robust against network hiccups — a failed poll is silent.
 *   4. Zero effect if service worker isn't registered.
 *
 * How the update actually applies:
 *   • On startup we register the SW; when a new ``service-worker.js``
 *     lands the "installing" worker enters ``waiting`` state.
 *   • We detect this + also detect a hash mismatch in
 *     ``asset-manifest.json`` (belt-and-suspenders — some deploys
 *     change assets without changing the SW file).
 *   • Clicking Update posts ``SKIP_WAITING`` to the waiting worker
 *     and reloads once ``controllerchange`` fires.
 */

const MANIFEST_URL = '/asset-manifest.json';
const POLL_MS = 60_000;  // 60s — frequent enough for enterprise users,
                          //       rare enough to be free of network cost.


/** Read the bundle hash the *current* page loaded — baked into the
 *  HTML by CRA. We store the first-seen value so later polls have a
 *  baseline. Returns null before the first successful fetch. */
let _baselineMainJs = null;


async function _fetchManifest() {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    const data = await res.json();
    // CRA emits both ``files["main.js"]`` (hashed) and ``entrypoints``.
    return (data && data.files && data.files['main.js']) || null;
  } catch (_e) {
    return null;
  }
}


/**
 * Start polling for a new build. Calls ``onUpdate()`` exactly once
 * when a new version is detected. Returns a stop() cleanup fn.
 */
export function startUpdateChecker(onUpdate) {
  if (typeof window === 'undefined') return () => {};
  let stopped = false;

  const runOnce = async () => {
    const current = await _fetchManifest();
    if (!current) return;
    if (_baselineMainJs === null) {
      _baselineMainJs = current;
      return;
    }
    if (current !== _baselineMainJs && !stopped) {
      stopped = true;   // Fire onUpdate once, then quiet down.
      onUpdate({ from: _baselineMainJs, to: current });
    }
  };

  // Kick off immediately (learns the baseline), then poll on interval.
  runOnce();
  const timer = setInterval(runOnce, POLL_MS);

  // Also re-check whenever the tab becomes visible again — the user
  // may have been away for hours and a deploy happened in between.
  const onVisible = () => {
    if (document.visibilityState === 'visible') runOnce();
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    stopped = true;
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
  };
}


/**
 * Register the service worker + wire the "waiting worker" listener.
 * When Emergent deploys a new build, ``service-worker.js`` bytes
 * change → browser installs a new SW that ends up in ``waiting``.
 * We surface that to the caller so the UI can show "Update Now".
 */
export function registerServiceWorker(onWaitingWorker) {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  // NEVER register in local dev — CRA's dev server + SW = bad time.
  if (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => {
        // If a waiting worker already exists (e.g. the user
        // ignored the last banner and reopened the tab), surface it.
        if (reg.waiting && navigator.serviceWorker.controller) {
          onWaitingWorker(reg.waiting);
        }
        // Fresh SW discovered → track its lifecycle.
        reg.addEventListener('updatefound', () => {
          const nw = reg.installing;
          if (!nw) return;
          nw.addEventListener('statechange', () => {
            if (nw.state === 'installed' && navigator.serviceWorker.controller) {
              // A previous SW was already controlling — this one is
              // NEW and waiting to take over.
              onWaitingWorker(nw);
            }
          });
        });
      })
      .catch(() => { /* silent — SW is a nice-to-have */ });

    // Reload once the new SW takes control (fired after skipWaiting).
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });
  });
}


/**
 * Nuclear "Update App" action — for the Settings menu and for the
 * banner button. Tells the waiting SW to activate + on next
 * ``controllerchange`` the page reloads. If there is no waiting SW,
 * unregisters + hard-reloads as a fallback so support engineers
 * always have an escape hatch.
 */
export async function applyUpdate() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (reg && reg.waiting) {
      reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      return;  // ``controllerchange`` handler above reloads for us.
    }
    // Fallback path: no waiting SW, but the user asked for an update
    // (e.g. from a Settings > "Update App" click). Nuke every cache
    // and reload from network.
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if (reg) await reg.unregister();
  } finally {
    window.location.reload();
  }
}
